import { ButtonHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

type Variant = 'primary' | 'ghost' | 'quiet';
type Size = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3.5 text-md',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

/**
 * The ET. button. `primary` is the amber fill — reserve it for the single
 * next action on a viewport, per the brand's amber-scarcity rule.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, children, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-md font-body font-medium',
          'transition-all duration-200 ease-et-out disabled:opacity-40 disabled:pointer-events-none',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-dawn focus-visible:outline-offset-2',
          SIZE_CLASSES[size],
          variant === 'primary' &&
            'bg-dawn text-ink hover:bg-dawn-strong active:bg-dawn-strong shadow-elevate',
          variant === 'ghost' &&
            'border border-aurora/40 text-text hover:border-aurora hover:bg-aurora/5',
          variant === 'quiet' && 'text-muted hover:text-text',
          className
        )}
        {...rest}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
